import type { ListPopoverProps } from './index.types';
import React from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '~/lib/ui/popover';
import { cn } from '~/lib/utils';
import { ListPopoverItem } from './ListPopoverItem';

export const ListPopover: React.FC<ListPopoverProps> = ({
  side,
  options,
  children,
  classname,
  arrowOffset,
  align,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  hidden,
  avoidCollisions = true,
  closeOnScroll = false,
  scrollContainerId,
  triggerAsChild = false,
}) => {
  const [internalOpen, setInternalOpen] = React.useState(false);

  const isControlled = controlledOpen !== undefined;
  const isPopoverOpen = isControlled ? controlledOpen : internalOpen;

  const changeOpenState = React.useCallback(
    (open: boolean) => {
      if (isControlled) {
        controlledOnOpenChange?.(open);
      } else {
        setInternalOpen(open);
      }
    },
    [isControlled, controlledOnOpenChange]
  );

  React.useEffect(() => {
    if (!closeOnScroll || !isPopoverOpen) {
      return;
    }

    const handleScroll = () => changeOpenState(false);

    const target = scrollContainerId
      ? document.getElementById(scrollContainerId)
      : document;

    target?.addEventListener('scroll', handleScroll, { capture: true, passive: true });

    return () => {
      target?.removeEventListener('scroll', handleScroll, { capture: true });
    };
  }, [closeOnScroll, isPopoverOpen, scrollContainerId, changeOpenState]);

  // Radix focuses the trigger on close, which scrolls the list back to the
  // row that opened the popover during fast scroll. Prevented via onCloseAutoFocus
  const handleCloseAutoFocus = (event: Event) => {
    event.preventDefault();
  };

  return (
    <Popover open={isPopoverOpen} onOpenChange={changeOpenState}>
      <PopoverTrigger asChild={triggerAsChild}>{children}</PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        arrowOffset={arrowOffset}
        avoidCollisions={avoidCollisions}
        onCloseAutoFocus={handleCloseAutoFocus}
        className={cn('flex flex-col min-w-[190px]', classname, { '!hidden': hidden })}
      >
        {options.map((option, index) => (
          <ListPopoverItem
            key={option.testId ?? `${option.text}-${index}`}
            text={option.text}
            icon={option.icon}
            testId={option.testId}
            isSelected={option.isSelected}
            destructive={option.destructive}
            disabled={option.disabled}
            onClick={(e) => {
              changeOpenState(false);
              option.onClick?.(e);
            }}
          />
        ))}
      </PopoverContent>
    </Popover>
  );
};
